"use client";

import { cn } from "@/lib/utils";
import { AccordionList } from "@/packages/common/src/components/accordions/AccordionList";
import { Heading } from "@/packages/common/src/components/ui/Heading";
import { HoverBarMenuArray } from "@/packages/common/src/components/ui/HoverBarMenuArray";
import { LegalFooter } from "@/packages/common/src/components/ui/LegalFooter";
import { Tabs, TabsContent, TabsList } from "@/packages/common/src/components/ui/Tabs";
import { Card } from "@/packages/common/src/components/ui/card";
import useWindowSize from "@/packages/common/src/hooks/useWindowSize";
import { getElementUnusedHeight } from "@/packages/common/src/utils/DOM";
import { countElementsThatMeetPredicate } from "@/packages/common/src/utils/objectManipulation";
import { getCSSVariable } from "@/packages/common/src/utils/styles";
import { updateUrl } from "@/packages/common/src/utils/window";
import type { InfraSecurityChecks } from "@/prisma/prisma";
import PIPEDALogo from "@/public/imgs/compliance/PIPEDA.png";
import AWS_LOGO from "@/public/imgs/subProcessors/AWS_Logo.png";
import G_LOGO from "@/public/imgs/subProcessors/G_Logo.webp";
import MONGODB_LOGO from "@/public/imgs/subProcessors/MongoDB_Logomark_ForestGreen.png";
import OPENAI_LOGO from "@/public/imgs/subProcessors/Openai_Logo.png";
import STRIPE_LOGO from "@/public/imgs/subProcessors/Stripe_Logo.png";
import { type SecurityUpdate, securityUpdates } from "@/src/lib/data/securityUpdates";
import { ComplianceStandard } from "@common/components/security/ComplianceStandard";
import { flattenSecurityControls } from "@common/utils/securityControlsHelper";
import { type VariantProps, cva } from "class-variance-authority";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { forwardRef } from "react";
import { SubProcessor } from "../../../../common/src/components/security/SubProcessor";
import { ControlsDataTable } from "../dataTable/controlsDataTable";
import NavbarTrust from "../navbar/NavbarTrust";
import securityBestPractices from "./SecurityBestPractices";
import { SecurityUpdateCard } from "./SecurityUpdateCard";
import styles from "./TrustCenter.module.css";

const TrustCenterVariants = cva("", {
  variants: {
    variant: {
      default: "",
    },
    size: {
      default: "",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "default",
  },
});

interface TrustCenterProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof TrustCenterVariants> {
  className?: string;
  initialSecurityState?: InfraSecurityChecks;
  initialTab?: string;
}

type ChildHeightObject = {
  key: string;
  minHeight: number | number[];
};

export const TrustCenter = forwardRef<HTMLDivElement, TrustCenterProps>(
  ({ className, initialSecurityState, initialTab, variant, size, ...props }, ref) => {
    const controlsData = flattenSecurityControls();
    const menuBarItemTitles = [`Overview`, `Controls`, `Sub-processors`, `Best Practices`, `Updates`];
    const numberOfUpdates = countElementsThatMeetPredicate(securityUpdates, (updateObj: SecurityUpdate) => {
      //count object where the data is within the last 30 days
      const thirtyDaysAgo = new Date(new Date().getTime() - 30 * 24 * 60 * 60 * 1000);
      return new Date(updateObj.date) > thirtyDaysAgo;
    });
    const titleQuantities = [-1, controlsData.length, -1, -1, numberOfUpdates];

    //Every block needs a ref to get its height
    const titleRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const [childWidth, setChildWidth] = useState<number | undefined>(undefined);
    const [childHeights, setChildHeights] = useState<(ChildHeightObject | null)[]>([]);
    const [containerHeight, setContainerHeight] = useState(0);
    const [activeTab, setActiveTab] = useState<number>(
      initialTab ? menuBarItemTitles.findIndex((title) => title.toLowerCase() === initialTab.toLowerCase()) : 0
    );
    const [triggerSecondRender, setTriggerSecondRender] = useState(false);

    const dotSpacing = 16;

    const windowSize = useWindowSize();

    console.log("windowSize", windowSize);

    useEffect(() => {
      //set the url to include the tab
      if (activeTab !== undefined && menuBarItemTitles.length > 0) {
        const menuOpen = menuBarItemTitles[activeTab].toLowerCase();
        updateUrl(undefined, { tab: menuOpen });
      }
      setTimeout(() => {
        //setTriggerSecondRender(!triggerSecondRender);
      }, 200);
    }, [activeTab, menuBarItemTitles]);

    useEffect(() => {
      if (!windowSize) return;
      setTimeout(() => {
        if (containerRef.current) {
          const navbarHeightStr = getCSSVariable("--navbar-height");
          const navbarHeight = Number.parseInt(navbarHeightStr) || 0; // fallback to 0 if parsing fails
          setContainerHeight(containerRef.current.clientHeight - navbarHeight);
        }

        /* * * * * * * * * * *
         * Determine the nearest width of the container landing on a dotSpacing
         */
        const windowWidth = windowSize.innerWidth;

        const addDots = windowWidth < 850 ? 1 : 0;

        const dotCount = Math.floor(windowWidth / dotSpacing);
        //we want childWidth to be (dotCount - 2) * dotSpacing
        setChildWidth((dotCount + addDots) * dotSpacing);

        /* * * * * * * * * * *
         * Determine the nearest height of each child container landing on a dotSpacing
         */
        const childHeightObjects: ChildHeightObject[] = [];
        if (titleRef.current) {
          //Get the height of the container
          const titleHeight = titleRef.current?.clientHeight ?? 0;

          let titleHeightRounded = 0;

          //We get the num pixels which are empty in the container
          const unusedTitleHeight = getElementUnusedHeight(titleRef.current);
          if (unusedTitleHeight > dotSpacing) {
            //reduce the titleHeight by the unused height
            titleHeightRounded = titleHeight - unusedTitleHeight;
            //rounded titleHeight up to nearest 16px multiple
            titleHeightRounded = Math.ceil(titleHeightRounded / 16) * 16;
          } else {
            //The height is possibly too small to align on a dotSpacing...
            //rounded titleHeight up to nearest 16px multiple
            titleHeightRounded = Math.ceil(titleHeight / 16) * 16;
          }
          childHeightObjects.push({
            key: "security-center-title-container",
            minHeight: titleHeightRounded,
          });
        }

        setChildHeights(childHeightObjects);
      }, 20);
    }, [windowSize, activeTab, triggerSecondRender]);

    const getHeight = (childHeights?: (ChildHeightObject | null)[] | null, key?: string): number | undefined => {
      if (!childHeights || !key) return undefined;
      const childHeightObject = childHeights.find((childHeight) => childHeight?.key === key);
      if (childHeightObject) {
        if (Array.isArray(childHeightObject.minHeight)) {
          return Math.max(...childHeightObject.minHeight);
        }
        return childHeightObject.minHeight;
      }
      return undefined;
    };

    const cardClassNames = "mix-blend-plus-darker";

    const controlsJSX = (
      <Card className="overflow-x-scroll w-full">
        <Heading className="" info="The controls that we have in place to ensure the security of our platform">
          Controls
        </Heading>
        <ControlsDataTable currentUser={null} menuBarItemTitles={[]} controlsData={controlsData} />
      </Card>
    );

    return (
      <div ref={containerRef} className={cn("pt-navbar-height h-full w-full overflow-x-clip", styles.container)}>
        <NavbarTrust
          className="px-3.5"
          initialSecurityState={initialSecurityState}
          fixed={true}
          showUserLoading={false}
          subTitle="Trust Center"
          statusDot={true}
        />
        <div style={{ height: containerHeight }} className="absolute w-full -z-10 overflow-x-clip transition-all duration-1000">
          {" "}
          {/* overflow-clip is needed to prevent the semiCircles and blows from overflowing and causing x scroll */}
          <div
            key={"backgroundGrid"}
            className={`absolute -z-20 inset-0 w-full bg-[radial-gradient(#e5e7eb_1px,transparent_1.3px)] [background-size:16px_16px]`}
            hidden={true}
          />
          <div
            key={"semiCircle"}
            className={cn(
              "absolute -top-10 right-0 w-full h-full scale-y-110 translate-x-0 md:-translate-x-[5%] opacity-70 blur-xl mix-blend-normal",
              styles.semiCircle
            )}
          />
          <div
            key={"organicBlob"}
            hidden={false}
            className={cn("absolute -top-10 right-0 w-[45vw] h-[45vw] opacity-30 blur-3xl mix-blend-normal", styles.organicBlob)}
          />
          <div
            key={"blueBlob1"}
            hidden={false}
            className={cn("absolute top-[35vh] -right-10 w-[30vw] h-[30vw] opacity-30 blur-xl rounded-full mix-blend-multiply", styles.blob1)}
          />
          <div
            key={"blueBlob2"}
            hidden={true}
            className={cn("absolute top-1 right-[520px] w-[30vw] h-[30vw] opacity-70 rounded-full bg-radial-gradient-blob-2 blur-xl", styles.blob2, "")}
          />
          <div
            key={"yellowBlob"}
            hidden={true}
            className={cn("absolute bottom-1/4 left-1/3 w-[45vw] h-[45vw] rounded-full bg-radial-gradient-blob-3 opacity-100 blur-xl", styles.blob3)}
          />
        </div>

        <div
          key={"security-center-text-container"}
          className="px-6 pt-6 flex flex-col gap-2 z-10 justify-between h-full max-w-full sm:max-w-[640px] md:max-w-[960px] xl:max-w-[1340px] 2xl:max-w-[1520px] mx-auto"
          style={{ width: childWidth }}
        >
          <Card
            key={"security-center-title-container"}
            style={{
              minHeight: getHeight(childHeights, "security-center-title-container"),
            }}
            ref={titleRef}
            className=""
          >
            <Heading>About</Heading>
            <p className="mt-3 text-secondary-dark">
              Refdocs is a secure healthcare technology platform designed to enhance efficiency and accuracy in report generation for dental specialists.
              By leveraging structured data from patient appointments, Refdocs automates the creation of detailed, professional reports, reducing
              administrative workload and improving communication with referring dentists. Security and compliance are at the core of what we do. Refdocs
              employs industry-leading safeguards, including end-to-end encryption, multi-factor authentication, and full compliance with PIPEDA and other
              relevant data protection regulations. Our platform is designed to centralize communication and streamline workflows while maintaining strict
              access controls to ensure patient data remains protected. With a user-friendly interface and seamless automation of reporting, template
              management, and communication processes, Refdocs allows dental professionals to focus on patient care while maintaining the highest standards
              of data security and operational efficiency.
            </p>
          </Card>
          <Tabs className="flex flex-col gap-2" value={String(activeTab)}>
            <TabsList variant="blank" className="">
              <HoverBarMenuArray
                className="w-full border-b-[2px] border-border overflow-x-scroll"
                //onClick={handleMenuBarItemClicked}
                menuBarItemTitles={menuBarItemTitles}
                menuBarItemIsTabTrigger={true}
                onTabClick={(e, index) => setActiveTab(index)}
                titleQuantities={titleQuantities}
                hideNegativeQuantities={true}
                defaultTabIndex={initialTab ? menuBarItemTitles.findIndex((title) => title.toLowerCase() === initialTab.toLowerCase()) : 0}
                allowUnselection={false}
                disableFocusUnderline={false}
              />
            </TabsList>
            <TabsContent key={String(0)} variant="blank" value={String(0)}>
              <div className="flex flex-col md:flex-row gap-2">
                <Card className={cn("h-min min-w-[200px] max-w-full md:max-w-[24vw]", cardClassNames)}>
                  <Heading
                    className=""
                    headingClassName="whitespace-normal shrink"
                    info="The compliance standards that we follow, and routinely self-audit against"
                  >
                    Compliance Standards
                  </Heading>
                  <ComplianceStandard standard="PIPEDA" logo={PIPEDALogo} />
                  <ComplianceStandard standard="ISO 27001" />
                  <ComplianceStandard standard="SOC 2 Type 1" />
                  <ComplianceStandard standard="PCI DSS v3.2.1" />
                  <ComplianceStandard standard="HIPAA" />
                  <ComplianceStandard standard="CIS AWS Foundations Benchmark" />
                  <ComplianceStandard standard="AWS Foundational Security Best Practices v1.0.0" />
                </Card>
                {controlsJSX}
              </div>
            </TabsContent>
            <TabsContent key={String(1)} variant="blank" value={String(1)}>
              {controlsJSX}
            </TabsContent>
            <TabsContent key={String(2)} variant="blank" value={String(2)}>
              <Card className="flex flex-col gap-2 px-0">
                <SubProcessor
                  logo={AWS_LOGO}
                  heading="Amazon Web Services"
                  subHeading="Cloud and Infrastructure Provider"
                  description="Comprehensive cloud computing platform offering a wide array of services, including compute, storage, databases, analytics, and more, used by world-class companies to innovate faster and power their businesses."
                  link="https://aws.amazon.com/"
                  className="border-b border-border px-4 pb-2 min-h-[80px] flex"
                />
                <SubProcessor
                  logo={MONGODB_LOGO}
                  heading="MongoDB"
                  subHeading="Database Provider"
                  description="Worlds most popular, cross-platform, document-oriented database provider. Classified as a NoSQL database product, MongoDB uses JSON-like documents with optional schemas."
                  link="https://www.mongodb.com/"
                  className="border-b border-border px-4 pb-2 min-h-[80px] flex"
                  imageClassName="aspect-auto max-w-[22px] mx-[8px]"
                />
                <SubProcessor
                  logo={G_LOGO}
                  heading="Google Workspace"
                  subHeading="Cloud Storage and Collaboration Platform"
                  description="Google Workspace is a comprehensive cloud computing platform offering a wide array of services, including compute, storage, databases, analytics, and more, used by world-class companies to innovate faster and power their businesses."
                  link="https://workspace.google.com/"
                  className="border-b border-border px-4 pb-2 min-h-[80px] flex"
                />
                <SubProcessor
                  logo={OPENAI_LOGO}
                  heading="OpenAI"
                  subHeading="AI Development Services"
                  description="OpenAI is a research laboratory dedicated to building safe and beneficial AGI."
                  link="https://openai.com/"
                  className="border-b border-border px-4 pb-2 min-h-[80px] flex"
                />
                <SubProcessor
                  heading="Microsoft Azure"
                  subHeading="AI Speech Services"
                  description="Azure Cognitive Services provides HIPAA-eligible speech-to-text transcription. Audio is processed in real-time and not retained by Azure after processing. BAA in place for HIPAA compliance."
                  link="https://azure.microsoft.com/en-us/products/ai-services/ai-speech"
                  className="border-b border-border px-4 pb-2 min-h-[80px] flex"
                />
                <SubProcessor
                  logo={STRIPE_LOGO}
                  heading="Stripe"
                  subHeading="Payment Processing"
                  description="Stripe is a comprehensive payment processing platform that allows businesses to accept payments from customers around the world."
                  link="https://stripe.com/"
                  className="px-4 min-h-[80px] flex"
                />
              </Card>
            </TabsContent>
            <TabsContent key={String(3)} variant="blank" value={String(3)}>
              <Card>
                <AccordionList items={securityBestPractices} contentClassName="pb-1 sm:pb-2 md:pb-3" accordionItemClassName="px-1 md:px-3" />
              </Card>
            </TabsContent>
            <TabsContent key={String(4)} variant="blank" value={String(4)}>
              <Card>
                {securityUpdates.map((update, index, array) => (
                  <SecurityUpdateCard key={index} update={update} />
                ))}
              </Card>
            </TabsContent>
          </Tabs>
          <LegalFooter className="" />
        </div>
      </div>
    );
  }
);

TrustCenter.displayName = "TrustCenter";
